import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { cn } from '@worldcoo/ui/lib/utils';
import { Header } from './header';
import { HeroPending } from './hero';
import { CampaignsGridPending } from './campaigns';
import { CampaignHeroPending } from './campaign-hero';
import {
	BACK_LINK_ROW_CLASS,
	CampaignBackNav,
	CampaignBottomSection,
	CampaignColumns,
	CampaignLayout,
	CampaignTopSection,
	HeroSection,
	HomeLayout,
	HomeMain,
	CampaignsSection,
	LegalLayout,
	LEGAL_CONTENT_CLASS,
} from './page-shell';

/**
 * Route-owned pending UI for the portal's public pages, set as each route's `pendingComponent`
 * (same pattern as the donate steps; see donate-step-pending.tsx). Shown while the route's
 * loader or data suspends, in place of the router-wide default that renders nothing.
 *
 * Each pending renders the REAL `<Header />` rather than a skeleton bar: the header resolves its
 * own data behind its own boundary, so on a client navigation (metadata already cached) the
 * actual logo and actions paint immediately and only the content area is a skeleton.
 *
 * The page geometry comes from the same shells the routes themselves render (page-shell.tsx), and
 * the sections that already own a skeleton for their own suspense boundary export it rather than
 * being redrawn here. Only the parts with nothing to reuse are skeletons written out below.
 */

export function HomePending() {
	return (
		<HomeLayout>
			<HomeMain>
				<Header />
				<HeroSection>
					<HeroPending />
				</HeroSection>

				<CampaignsSection>
					<CampaignsGridPending />
				</CampaignsSection>
			</HomeMain>
		</HomeLayout>
	);
}

export function CampaignPending() {
	return (
		<CampaignLayout>
			<Header />
			<CampaignTopSection>
				<CampaignBackNav>
					<div className={BACK_LINK_ROW_CLASS}>
						<Skeleton className="h-6 w-40" />
					</div>
				</CampaignBackNav>

				<HeroSection>
					<CampaignHeroPending />
				</HeroSection>
			</CampaignTopSection>

			<CampaignBottomSection>
				<CampaignColumns>
					<div className="flex w-full flex-col gap-4 lg:w-2/3">
						<Skeleton className="h-8 w-56" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-4/5" />
						<Skeleton className="h-64 w-full rounded-xl" />
					</div>
					<aside className="w-full flex-1">
						<Skeleton className="h-80 w-full rounded-xl" />
					</aside>
				</CampaignColumns>
			</CampaignBottomSection>
		</CampaignLayout>
	);
}

export function LegalPagePending() {
	return (
		<LegalLayout>
			<Header />
			<div className={cn(LEGAL_CONTENT_CLASS, 'flex flex-col gap-3')}>
				<Skeleton className="mb-4 h-8 w-64" />
				{Array.from({ length: 10 }).map((_, index) => (
					// oxlint-disable-next-line react/no-array-index-key
					<Skeleton key={index} className={index % 4 === 3 ? 'h-4 w-2/3' : 'h-4 w-full'} />
				))}
			</div>
		</LegalLayout>
	);
}
